/**
 * useDocumentVisible — pause expensive presence animations when the page
 * is hidden / tab is backgrounded.
 *
 * Closes Phase 1 strict-bar Animator FU-23: `breathingIdle` is an infinite
 * loop; if it runs while the tab is hidden the GPU continues to spend
 * cycles on an animation no one is watching. Consumers wrap their
 * `animate=...` prop in a conditional driven by this hook so the loop is
 * gated by visibility. Combined with the root `<MotionConfig
 * reducedMotion="user">` wrap in BriefingFirstLanding, the result is:
 *
 *   • prefers-reduced-motion: reduce  → no loop at all (root collapses it)
 *   • document hidden                  → no loop (this hook stops it)
 *   • else                             → loop runs at normal cadence
 *
 * Future Phase 5 Felix-avatar persistent presence will sit on top of this.
 */

import { useEffect, useState } from "react";

export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof document === "undefined") return true;
    return document.visibilityState !== "hidden";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const handler = () => {
      setVisible(document.visibilityState !== "hidden");
    };
    document.addEventListener("visibilitychange", handler);
    handler();
    return () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }, []);

  return visible;
}
